import { getRelativeFontSize, mediumFont, theme } from "../../../utils/styles";

/**
 * Layout-only styles. Everything a MUI component can own — colours, radii,
 * typography, interaction states — lives in `astroTheme.ts` instead.
 *
 * The page follows the full-bleed shell used by About Vedaz: a sticky glass nav
 * strip over an edge-to-edge map, with no card frame around either. The control
 * drawer is pinned flush to the map's left edge and spans its full height.
 */

const NAV_HEIGHT = 56;
const DOCK_WIDTH = 344;
const DOCK_RAIL_WIDTH = 56;

const astrocartographyStyles = {
  /* ------------------------------- page shell ----------------------------- */

  pageContainer: {
    display: "flex",
    flexDirection: "column",
  },

  navWrapper: {
    position: "sticky" as const,
    top: 0,
    zIndex: 1099,
    background:
      "linear-gradient(180deg, rgba(20,12,40,0.98) 0%, rgba(8,8,22,0.97) 100%)",
    backdropFilter: "blur(28px)",
    WebkitBackdropFilter: "blur(28px)",
    borderBottom: "1px solid rgba(144,83,246,0.22)",
    boxShadow: "0 4px 32px rgba(0,0,0,0.55)",
  },
  navRow: {
    display: "flex",
    alignItems: "center",
    gap: { xs: "6px", sm: "10px" },
    px: { xs: 1.5, sm: 2.5 },
    height: `${NAV_HEIGHT}px`,
  },
  navBack: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    flexShrink: 0,
    borderRadius: "10px",
    px: "10px",
    py: "6px",
    color: "rgba(255,255,255,0.85)",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    "&:hover": {
      background: "rgba(255,255,255,0.1)",
      borderColor: "rgba(255,255,255,0.2)",
      transform: "translateX(-1px)",
    },
  },
  navSeparator: {
    width: "1px",
    height: "22px",
    background: "rgba(255,255,255,0.1)",
    flexShrink: 0,
    mx: 0.5,
  },
  navBrand: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    minWidth: 0,
  },
  navMark: {
    width: 34,
    height: 34,
    fontSize: "18px",
    flexShrink: 0,
    color: "#C49CF8",
    bgcolor: "rgba(144,83,246,0.16)",
    border: "1px solid rgba(144,83,246,0.4)",
    boxShadow: "0 0 16px rgba(144,83,246,0.28)",
    fontFamily: '"Segoe UI Symbol", "Noto Sans Symbols", serif',
  },
  navTitle: {
    ...mediumFont,
    fontSize: getRelativeFontSize(2),
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    lineHeight: 1.15,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: { fontSize: getRelativeFontSize(-1) },
  },
  navSubtitle: {
    lineHeight: 1.3,
    [theme.breakpoints.down("md")]: { display: "none" },
  },
  navSpacer: { flex: 1, minWidth: "4px" },
  navLegend: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    flexShrink: 0,
    px: 1.25,
    py: 0.75,
    borderRadius: "12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    [theme.breakpoints.down("md")]: { display: "none" },
  },
  navLegendValue: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-2),
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  navLegendDivider: { height: 16, alignSelf: "center" },
  navAction: {
    flexShrink: 0,
    width: 36,
    height: 36,
    color: "rgba(255,255,255,0.8)",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    "&:hover": {
      background: "rgba(144,83,246,0.25)",
      borderColor: "rgba(144,83,246,0.55)",
      color: "#FFFFFF",
    },
  },

  /* -------------------------------- map area ------------------------------ */

  stage: {
    position: "relative",
    // Fills everything below the app header and this page's nav strip.
    height: `calc(100vh - 120px - ${NAV_HEIGHT}px)`,
    minHeight: "440px",
    bgcolor: "background.default",
    borderBottom: "1px solid rgba(144,83,246,0.18)",
    [theme.breakpoints.down("sm")]: {
      height: `calc(100vh - 100px - ${NAV_HEIGHT}px)`,
    },
    // In fullscreen the stage is the whole tab, so it owns the viewport.
    "&:fullscreen": {
      height: "100vh",
      minHeight: "100vh",
      borderBottom: "none",
    },
  },
  mapCanvas: { width: "100%", height: "100%" },
  stageGlow: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    // Above the tiles, beneath the planetary lines (Leaflet's overlay pane).
    zIndex: 350,
    background:
      "radial-gradient(120% 80% at 0% 0%, rgba(144,83,246,0.14) 0%, transparent 55%)," +
      "radial-gradient(120% 80% at 100% 100%, rgba(6,182,212,0.11) 0%, transparent 55%)",
    boxShadow: "inset 0 0 110px rgba(6,4,18,0.5)",
  },
  /** Floating map action, e.g. the fullscreen toggle. */
  stageAction: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 900,
    width: 38,
    height: 38,
    color: "rgba(255,255,255,0.85)",
    bgcolor: "rgba(10, 8, 24, 0.86)",
    backdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
    "&:hover": {
      bgcolor: "rgba(144,83,246,0.35)",
      borderColor: "rgba(144,83,246,0.6)",
      color: "#FFFFFF",
    },
  },

  /* ----------------------------- control drawer --------------------------- */

  dock: {
    position: "absolute",
    // Flush to the map's left edge, top to bottom.
    top: 0,
    left: 0,
    bottom: 0,
    // Above the state overlay so the controls stay usable while it shows.
    zIndex: 1099,
    width: `${DOCK_WIDTH}px`,
    maxWidth: "88%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 0,
    borderRight: "1px solid rgba(144, 83, 246, 0.3)",
    bgcolor: "rgba(10, 8, 24, 0.88)",
    backdropFilter: "blur(18px)",
    boxShadow: "8px 0 40px rgba(0,0,0,0.45)",
    overflow: "hidden",
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.standard,
    }),
  },
  dockCollapsed: { width: `${DOCK_RAIL_WIDTH}px` },
  dockHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    px: 1.5,
    py: 1.25,
    flexShrink: 0,
    borderBottom: "1px solid",
    borderColor: "divider",
    backgroundImage:
      "linear-gradient(135deg, rgba(144,83,246,0.18) 0%, transparent 100%)",
  },
  profileBlock: {
    px: 1.5,
    py: 1.5,
    flexShrink: 0,
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  /**
   * The single scroll region on the page. Block flow, not flex: as flex items
   * the sections would shrink to fit the drawer's fixed height instead of
   * overflowing it, which clipped their content whenever one expanded.
   */
  dockBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    p: 1.25,
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      bgcolor: "rgba(144,83,246,0.4)",
    },
    "&::-webkit-scrollbar-thumb:hover": { bgcolor: "rgba(144,83,246,0.75)" },
  },
  dockRail: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.75,
    py: 1.25,
  },
  railButton: {
    width: 38,
    height: 38,
    color: "var(--astro-accent)",
    bgcolor: "var(--astro-accent-soft)",
    border: "1px solid var(--astro-accent-soft)",
    "&:hover": {
      borderColor: "var(--astro-accent-glow)",
      boxShadow: "0 0 16px var(--astro-accent-soft)",
    },
  },

  /* ------------------------------- sections ------------------------------- */

  sectionIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: "10px",
    color: "var(--astro-accent)",
    bgcolor: "var(--astro-accent-soft)",
    border: "1px solid var(--astro-accent-glow)",
    "& .MuiSvgIcon-root": { fontSize: "17px" },
  },

  /* -------------------------------- profile ------------------------------- */

  profileAvatar: {
    width: 36,
    height: 36,
    fontSize: "15px",
    flexShrink: 0,
    background:
      "linear-gradient(135deg, #9053F6 0%, #5E75FF 60%, #2CF1DF 100%)",
    boxShadow: "0 0 16px rgba(144,83,246,0.45)",
  },

  /* ------------------------------ basemap tiles --------------------------- */

  baseMapGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 1,
    width: "100%",
    "& .MuiToggleButton-root": {
      position: "relative",
      display: "block",
      p: 0,
      overflow: "hidden",
      textAlign: "center",
    },
  },
  baseMapPreview: (fill: string) => ({
    height: 42,
    background: `${fill}, repeating-linear-gradient(0deg, rgba(255,255,255,0.09) 0 1px, transparent 1px 11px), repeating-linear-gradient(90deg, rgba(255,255,255,0.09) 0 1px, transparent 1px 11px)`,
  }),
  baseMapLabel: { display: "block", py: 0.6, fontSize: "11px" },
  baseMapCheck: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 17,
    height: 17,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "var(--astro-accent)",
    boxShadow: "0 0 10px var(--astro-accent)",
    "& .MuiSvgIcon-root": { fontSize: "11px", color: "#0a0818" },
  },

  /* -------------------------------- planets ------------------------------- */

  planetGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(94px, 1fr))",
    gap: 0.75,
  },
  planetChip: (color: string, isVisible: boolean) => ({
    width: "100%",
    height: 32,
    justifyContent: "flex-start",
    cursor: "pointer",
    bgcolor: isVisible ? `${color}22` : "rgba(255,255,255,0.03)",
    borderColor: isVisible ? `${color}88` : "rgba(255,255,255,0.09)",
    color: isVisible ? "text.primary" : "text.disabled",
    boxShadow: isVisible ? `0 0 12px ${color}33` : "none",
    transition: "all 0.2s ease",
    "&:hover": { bgcolor: `${color}33`, borderColor: color },
    "& .MuiChip-label": {
      pl: 0.75,
      pr: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      ml: 0.75,
    },
  }),
  planetGlyph: (color: string, isVisible: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    ml: "5px",
    flexShrink: 0,
    borderRadius: "50%",
    fontSize: "12px",
    lineHeight: 1,
    fontFamily: '"Segoe UI Symbol", "Noto Sans Symbols", serif',
    color: isVisible ? "#FFFFFF" : "rgba(255,255,255,0.4)",
    bgcolor: isVisible ? color : "rgba(255,255,255,0.08)",
    boxShadow: isVisible ? `0 0 9px ${color}` : "none",
    transition: "all 0.2s ease",
  }),

  /* ------------------------------ city search ----------------------------- */

  fieldLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "text.secondary",
    textTransform: "uppercase",
    mb: 0.75,
  },
  angleGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 0.5,
    width: "100%",
  },

  /* -------------------------------- results ------------------------------- */

  resultsSummary: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    p: 1.25,
    mb: 1,
    borderRadius: "12px",
    bgcolor: "var(--astro-accent-soft)",
    border: "1px solid var(--astro-accent-glow)",
  },
  resultsCount: {
    fontSize: "24px",
    lineHeight: 1,
    color: "var(--astro-accent)",
    fontVariantNumeric: "tabular-nums",
  },
  resultsList: { py: 0 },
  cityRank: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    flexShrink: 0,
    borderRadius: "7px",
    fontSize: "10px",
    color: "var(--astro-accent)",
    bgcolor: "var(--astro-accent-soft)",
    border: "1px solid var(--astro-accent-soft)",
    fontVariantNumeric: "tabular-nums",
  },
  cityDistance: {
    fontSize: "11px",
    color: "var(--astro-accent)",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 0.75,
    py: 2,
  },
  emptyIcon: { color: "text.disabled", fontSize: "28px" },

  /* -------------------------------- overlay ------------------------------- */

  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1098,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    px: 3,
    background:
      "radial-gradient(70% 60% at 50% 45%, rgba(20,14,45,0.72) 0%, rgba(6,4,18,0.9) 100%)",
    backdropFilter: "blur(5px)",
  },
  overlayCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 1.5,
    maxWidth: 440,
    px: 4,
    py: 4,
    borderRadius: "22px",
    border: "1px solid rgba(144,83,246,0.3)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.55)",
  },
  overlayIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 60,
    height: 60,
    borderRadius: "18px",
    color: "var(--astro-accent)",
    bgcolor: "var(--astro-accent-soft)",
    border: "1px solid var(--astro-accent-glow)",
    "& .MuiSvgIcon-root": { fontSize: "29px" },
  },
  overlayOrbit: {
    position: "relative",
    width: 60,
    height: 60,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "21px",
    color: "var(--astro-accent)",
    fontFamily: '"Segoe UI Symbol", "Noto Sans Symbols", serif',
    border: "1px dashed var(--astro-accent-glow)",
    "&::after": {
      content: '""',
      position: "absolute",
      top: -4,
      left: "50%",
      width: 8,
      height: 8,
      borderRadius: "50%",
      bgcolor: "#2CF1DF",
      boxShadow: "0 0 12px #2CF1DF",
      transformOrigin: "0 34px",
      animation: "astroOrbit 2.4s linear infinite",
    },
  },
} as const;

export { DOCK_RAIL_WIDTH, DOCK_WIDTH, NAV_HEIGHT };
export default astrocartographyStyles;
